import {useCallback, useEffect} from "react";
import {Swap, V2PoolInfo} from "@tinymanorg/tinyman-js-sdk";

import useAsyncProcess from "../../../../core/network/async-process/useAsyncProcess";
import {useSwapFormDispatch} from "../../context/SwapFormContext";
import {
  generateSwapQuoteAssetData,
  generateSwapQuoteErrorMessage,
  getSwapTypeFromFormState
} from "../../SwapForm.utils";
import {useSwapPool} from "./swapPoolHooks";
import {SwapFormState} from "../../SwapForm.types";
import {useSwapFlowState} from "../../../flow/context/SwapFlowContext";
import {SwapFlowView} from "../../../flow/context/SwapFlowContext.types";
import {PREFERRED_NETWORK, SWAP_SLIPPAGE} from "../../../../core/constants";

interface useSwapQuoteProps {
  assetIn: SwapFormState["assetIn"];
  assetOut: SwapFormState["assetOut"];
}

function useFetchSwapQuote({assetIn, assetOut}: useSwapQuoteProps) {
  const fetchSwapQuote = useCallback(
    async (pool: V2PoolInfo | null) => {
      try {
        if (assetOut.asset && assetIn.asset) {
          const swapType = getSwapTypeFromFormState({assetOut});

          const assetIN_asset = assetIn.asset;
          const assetOUT_asset = assetOut.asset;

          const {amount: quoteAmount} = generateSwapQuoteAssetData({
            assetIn,
            assetOut
          });

          if (quoteAmount === null) {
            return null;
          }

          const swapQuote = await Swap.v2.getQuote({
            type: swapType,
            amount: quoteAmount,
            pool,
            assetIn: {id: Number(assetIN_asset.id), decimals: assetIN_asset.decimals},
            assetOut: {id: Number(assetOUT_asset.id), decimals: assetOUT_asset.decimals},
            network: PREFERRED_NETWORK,
            slippage: SWAP_SLIPPAGE
          });

          return swapQuote;
        }
      } catch (error: any) {
        throw new Error(
          generateSwapQuoteErrorMessage({
            error,
            assetIn: assetIn.asset
          })
        );
      }

      return null;
    },
    [assetIn, assetOut]
  );

  return fetchSwapQuote;
}

function useFetchAndSaveQuote(
  assetIn: SwapFormState["assetIn"],
  assetOut: SwapFormState["assetOut"]
) {
  const {view} = useSwapFlowState();
  const dispatch = useSwapFormDispatch();
  const fetchQuote = useFetchSwapQuote({
    assetIn,
    assetOut
  });
  const {state, runAsyncProcess} = useAsyncProcess();
  const fetchSwapPoolAsyncState = useSwapPool();

  const fetchAndSave = useCallback(async () => {
    if (!fetchSwapPoolAsyncState.isRequestFetched) {
      return;
    }

    try {
      const quoteData = await fetchQuote(fetchSwapPoolAsyncState.data ?? null);

      dispatch({
        type: "SET_QUOTE",
        quote: quoteData
      });
    } catch (error: any) {
      dispatch({
        type: "SET_QUOTE",
        quote: null
      });

      throw error;
    }
  }, [
    fetchQuote,
    dispatch,
    fetchSwapPoolAsyncState.data,
    fetchSwapPoolAsyncState.isRequestFetched
  ]);

  useEffect(() => {
    if (view === SwapFlowView.Form) {
      runAsyncProcess(fetchAndSave());
    }
  }, [fetchAndSave, runAsyncProcess, view]);

  return {
    ...state,
    isRequestPending: state.isRequestPending || fetchSwapPoolAsyncState.isRequestPending
  };
}

export {useFetchSwapQuote, useFetchAndSaveQuote};
